import { useMutation, useQuery } from '@tanstack/react-query';
import { post, get, del } from '@/lib/api-client';

interface ExpireHoldsResult {
  success: boolean;
  message: string;
  data: {
    expiredLineItems: number;
    timestamp: string;
  };
}

export function useExpireHolds() {
  return useMutation({
    mutationFn: async () => {
      const response = await post<{ success: boolean; message: string; data: ExpireHoldsResult['data'] }>(
        '/charting/admin/expire-holds'
      );
      return response;
    },
  });
}

interface TestEmailParams {
  userId: string;
  subject: string;
  message: string;
}

interface TestEmailResult {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    messageId: string;
    recipient: string;
    timestamp: string;
  };
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (params: TestEmailParams) => {
      const response = await post<TestEmailResult>('/admin/test-email', params);
      return response;
    },
  });
}

interface EmailStatusResult {
  success: boolean;
  data: {
    configured: boolean;
    fromEmail: string;
    region: string;
    bounceStats: {
      totalBounces: number;
      hardBounces: number;
      softBounces: number;
      complaints: number;
    };
    recentBounces: Array<{
      id: string;
      email: string;
      bounceType: string;
      timestamp: string;
      reason?: string;
    }>;
  };
}

export function useEmailStatus() {
  return useQuery({
    queryKey: ['admin', 'email-status'],
    queryFn: async () => {
      const response = await get<EmailStatusResult>('/admin/email-status');
      return response.data;
    },
  });
}


interface PreviewEmailParams {
  holdRequestId: string;
  notificationType: 'hold_request_created' | 'hold_request_completed' | 'line_item_assigned' | 'line_item_status_changed' | 'hold_request_overdue' | 'exception_approval_required';
  recipientEmail: string;
}

interface PreviewEmailResult {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    messageId: string;
    recipient: string;
    notificationType: string;
    holdRequestId: string;
    requestNumber: string;
    subject: string;
    timestamp: string;
  };
}

export function usePreviewEmail() {
  return useMutation({
    mutationFn: async (params: PreviewEmailParams) => {
      const response = await post<PreviewEmailResult>('/admin/preview-email', params);
      return response;
    },
  });
}

interface ServiceApiKeyStatus {
  configured: boolean;
  source: 'db' | 'env' | 'none';
  prefix?: string;
  lastFour?: string;
  createdAt?: string;
}

interface ServiceApiKeyStatusResult {
  success: boolean;
  data: ServiceApiKeyStatus;
}

export function useServiceApiKeyStatus() {
  return useQuery({
    queryKey: ['admin', 'service-api-key-status'],
    queryFn: async () => {
      const response = await get<ServiceApiKeyStatusResult>('/admin/service-api-key');
      return response.data;
    },
  });
}

interface GenerateServiceApiKeyResult {
  success: boolean;
  data: {
    apiKey: string;
    prefix: string;
    lastFour: string;
    createdAt: string;
  };
}

export function useGenerateServiceApiKey() {
  return useMutation({
    mutationFn: async () => {
      const response = await post<GenerateServiceApiKeyResult>('/admin/service-api-key');
      return response;
    },
  });
}

interface RevokeServiceApiKeyResult {
  success: boolean;
}

export function useRevokeServiceApiKey() {
  return useMutation({
    mutationFn: async () => {
      const response = await del<RevokeServiceApiKeyResult>('/admin/service-api-key');
      return response;
    },
  });
}
